import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Hours } from 'models/organization';

type LabHolidaysProps = {
    holidayDays: number[];
    unavailableLabDates: number[];
    openingHours: Hours[];
};

const HOLIDAY_COLOR = '#FF9800';
const UNAVAILABLE_COLOR = '#f07a71';

const WEEK_DAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const DAY_NAME_TO_INDEX: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
};

const getMondayFirstOffset = (year: number, monthIndex: number): number => {
    const firstDay = new Date(year, monthIndex, 1).getDay();
    return (firstDay + 6) % 7;
};

type DayCell = {
    dayNumber: number;
    isHoliday: boolean;
    isUnavailable: boolean;
    isWorkingDay: boolean;
    isToday: boolean;
};

type MonthData = { year: number; monthName: string; offset: number; cells: DayCell[] };

const LabHolidays: FC<LabHolidaysProps> = ({ holidayDays, unavailableLabDates, openingHours }) => {
    const { t: translate } = useTranslation(['organization', 'lab-settings']);
    const [visibleMonthIndex, setVisibleMonthIndex] = useState(0);
    const today = new Date();

    const workingDayIndices = new Set<number>(
        (openingHours || [])
            .filter((hours) => hours.active)
            .map((hours) => DAY_NAME_TO_INDEX[hours.day.toLowerCase()])
            .filter((dayIndex) => dayIndex !== undefined),
    );

    const buildDateKey = (year: number, jsMonthIndex: number, day: number) => `${year}-${jsMonthIndex}-${day}`;

    const holidaySet = new Set<string>(
        (holidayDays || []).map((ts) => {
            const date = new Date(ts);
            return buildDateKey(date.getFullYear(), date.getMonth(), date.getDate());
        }),
    );

    const unavailableSet = new Set<string>(
        (unavailableLabDates || []).map((ts) => {
            const date = new Date(ts);
            return buildDateKey(date.getFullYear(), date.getMonth(), date.getDate());
        }),
    );

    const buildMonthData = (year: number, monthIndex: number): MonthData => {
        const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
        const monthName = new Date(year, monthIndex, 1).toLocaleString('en-US', { month: 'long' });
        const offset = getMondayFirstOffset(year, monthIndex);

        const cells: DayCell[] = Array.from({ length: daysInMonth }, (_, index) => {
            const dayNumber = index + 1;
            return {
                dayNumber,
                isHoliday: holidaySet.has(buildDateKey(year, monthIndex, dayNumber)),
                isUnavailable: unavailableSet.has(buildDateKey(year, monthIndex, dayNumber)),
                isWorkingDay: workingDayIndices.has(new Date(year, monthIndex, dayNumber).getDay()),
                isToday:
                    year === today.getFullYear() && monthIndex === today.getMonth() && dayNumber === today.getDate(),
            };
        });

        return { year, monthName, offset, cells };
    };

    const nextMonthDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const months: MonthData[] = [
        buildMonthData(today.getFullYear(), today.getMonth()),
        buildMonthData(nextMonthDate.getFullYear(), nextMonthDate.getMonth()),
    ];

    const currentMonth = months[visibleMonthIndex];
    const totalCells = currentMonth.offset + currentMonth.cells.length;
    const paddedCellCount = Math.ceil(totalCells / 7) * 7;

    const legend = [
        { color: HOLIDAY_COLOR, label: translate('organization:HOLIDAY') },
        { color: UNAVAILABLE_COLOR, label: translate('organization:UNAVAILABLE') },
    ];

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                {translate('organization:HOLIDAYS_AND_CLOSED_DAYS')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2.5, mb: 2, flexWrap: 'wrap' }}>
                {legend.map((item, legendIndex) => (
                    <Box key={legendIndex} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                backgroundColor: item.color,
                                borderRadius: '50%',
                                flexShrink: 0,
                            }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {item.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2.5,
                    maxWidth: 360,
                    bgcolor: 'background.paper',
                }}
            >
                {/* Month navigation */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <IconButton
                        size="small"
                        onClick={() => setVisibleMonthIndex(0)}
                        disabled={visibleMonthIndex === 0}
                        sx={{ visibility: visibleMonthIndex === 0 ? 'hidden' : 'visible' }}
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {currentMonth.monthName} {currentMonth.year}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => setVisibleMonthIndex(1)}
                        disabled={visibleMonthIndex === 1}
                        sx={{ visibility: visibleMonthIndex === 1 ? 'hidden' : 'visible' }}
                    >
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Weekday headers */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.75 }}>
                    {WEEK_DAY_LABELS.map((label) => (
                        <Typography
                            key={label}
                            variant="caption"
                            sx={{
                                textAlign: 'center',
                                fontWeight: 700,
                                color: 'text.disabled',
                                fontSize: '0.62rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.3,
                            }}
                        >
                            {label}
                        </Typography>
                    ))}
                </Box>

                {/* Day cells */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                    {Array.from({ length: paddedCellCount }, (_, cellIndex) => {
                        const dayIndex = cellIndex - currentMonth.offset;

                        if (dayIndex < 0 || dayIndex >= currentMonth.cells.length) {
                            return <Box key={`empty-${cellIndex}`} />;
                        }

                        const { dayNumber, isHoliday, isUnavailable, isWorkingDay, isToday } =
                            currentMonth.cells[dayIndex];

                        const isColored = isHoliday || isUnavailable;

                        return (
                            <Box
                                key={dayNumber}
                                sx={{
                                    aspectRatio: '1 / 1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    width: '100%',
                                    maxWidth: 34,
                                    borderRadius: '50%',
                                    fontSize: '0.72rem',
                                    fontWeight: isToday || isColored ? 700 : 400,
                                    ...(isHoliday && isUnavailable
                                        ? {
                                              background: `linear-gradient(90deg, ${HOLIDAY_COLOR} 50%, ${UNAVAILABLE_COLOR} 50%)`,
                                          }
                                        : {
                                              backgroundColor: isHoliday
                                                  ? HOLIDAY_COLOR
                                                  : isUnavailable
                                                    ? UNAVAILABLE_COLOR
                                                    : isToday
                                                      ? 'primary.main'
                                                      : 'transparent',
                                          }),
                                    color:
                                        isColored || isToday
                                            ? 'white'
                                            : isWorkingDay
                                              ? 'text.primary'
                                              : 'text.disabled',
                                }}
                            >
                                {dayNumber}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default LabHolidays;
